import { MemCommonEditorStore } from "@/components/note/store/mem-common-editor-store";
import {
  MDSButton,
  MDSButtonSize,
  MDSButtonVariant,
  MDSIconKind,
  MDSIconButton,
  MDSTooltip,
  MDSTooltipPlacement,
  MDSPopover,
  MDSText,
  MDSTextField,
  MDSTextFieldVariant,
  MDSTextColor,
  MDSTextVariant,
  MDSIconButtonSize,
} from "@/mds";
import styled from "@emotion/styled";
import { observer } from "mobx-react-lite";
import { type KeyboardEvent, type RefObject, useEffect, useRef } from "react";
import { useHotkey } from "@/domains/hotkeys/use-hotkey";
import { resolveHotkey } from "@/modules/keyboard-shortcuts/platform";
import { KEYBOARD_SHORTCUT_BINDINGS } from "@/modules/keyboard-shortcuts/registry";

interface FindInNoteModalProps {
  store?: MemCommonEditorStore;
  positionKey?: number;
  triggerRef?: RefObject<Element>;
}

export const FindInNoteModal = observer((props: FindInNoteModalProps) => {
  const { store, positionKey, triggerRef } = props;
  const isOpen = !!store?.findInNote?.isOpen;
  const close = store?.findInNote?.close;

  const findInputRef = useRef<HTMLInputElement>(null);
  const replaceInputRef = useRef<HTMLInputElement>(null);
  const hotkeyOptions = {
    enabled: isOpen,
    target: findInputRef,
  };

  useEffect(() => {
    if (!isOpen) return;

    findInputRef.current?.focus();
    findInputRef.current?.select();
  }, [isOpen, store?.findInNote?.focusRequestId]);

  useHotkey(
    resolveHotkey(KEYBOARD_SHORTCUT_BINDINGS.focus_previous_result),
    () => {
      store?.findInNote?.findPrevious();
    },
    hotkeyOptions
  );

  useHotkey(
    resolveHotkey(KEYBOARD_SHORTCUT_BINDINGS.find_in_note_next_result),
    () => {
      store?.findInNote?.findNext();
    },
    hotkeyOptions
  );

  useHotkey(
    resolveHotkey(KEYBOARD_SHORTCUT_BINDINGS.focus_next_result),
    () => {
      store?.findInNote?.findNext();
    },
    hotkeyOptions
  );

  useHotkey(
    resolveHotkey(KEYBOARD_SHORTCUT_BINDINGS.find_in_note_next_result_enter),
    () => {
      store?.findInNote?.findNext();
    },
    hotkeyOptions
  );

  useHotkey(
    resolveHotkey(KEYBOARD_SHORTCUT_BINDINGS.escape_to_close),
    () => {
      close?.();
    },
    hotkeyOptions
  );

  const findInNote = store?.findInNote;
  const canReplace = !!findInNote?.canReplace;

  const handleFindInputKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Tab" && !event.shiftKey && findInNote?.isReplaceMode) {
      event.preventDefault();
      replaceInputRef.current?.focus();
      return;
    }

    if (event.key === "Escape") {
      event.preventDefault();
      event.stopPropagation();
      close?.();
    }
  };

  const handleReplaceInputKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      event.preventDefault();
      event.stopPropagation();

      if (canReplace) {
        store?.replaceCurrentFindInNoteResult();
      }

      return;
    }

    if (event.key === "Escape") {
      event.preventDefault();
      event.stopPropagation();
      close?.();
    }
  };

  const handleOverlayKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== "Escape") return;

    event.preventDefault();
    event.stopPropagation();
    close?.();
  };

  return (
    <StyledPopover
      key={positionKey}
      isNonModal
      styling={false}
      triggerRef={triggerRef ?? store?.findInNote?.anchorRef}
      isOpen={!!isOpen}
      onOpenChange={value => {
        if (!value) close?.();
      }}
      shouldCloseOnInteractOutside={() => false}
      placement="bottom end"
    >
      <StyledOverlay $isOpen={isOpen} onKeyDown={handleOverlayKeyDown}>
        <FindRow>
          <Expanding>
            <FindInput
              aria-label="Find in note"
              autoFocus
              ref={findInputRef}
              variant={MDSTextFieldVariant.Form}
              placeholder="Find in note"
              value={findInNote?.searchQuery}
              onChange={findInNote?.setSearchQuery}
              inputProps={{
                onKeyDown: handleFindInputKeyDown,
              }}
            />
          </Expanding>
          {!!findInNote?.searchQuery && (
            <>
              <MatchCounterText color={MDSTextColor.Tertiary} variant={MDSTextVariant.TextS}>
                {findInNote.matchesCount
                  ? `${findInNote.currentMatchIndex + 1} of ${findInNote.matchesCount}`
                  : "Not found"}
              </MatchCounterText>
              {!!findInNote.matchesCount && (
                <>
                  <MDSTooltip
                    config={{
                      label: "Previous match",
                      placement: MDSTooltipPlacement.Top,
                    }}
                  >
                    <MDSIconButton
                      aria-label="Previous result"
                      size={MDSIconButtonSize.Small}
                      iconKind={MDSIconKind.AngleUp}
                      onClick={findInNote.findPrevious}
                    />
                  </MDSTooltip>
                  <MDSTooltip
                    config={{
                      label: "Next match",
                      placement: MDSTooltipPlacement.Top,
                    }}
                  >
                    <MDSIconButton
                      aria-label="Next result"
                      size={MDSIconButtonSize.Small}
                      iconKind={MDSIconKind.AngleDown}
                      onClick={findInNote.findNext}
                    />
                  </MDSTooltip>
                </>
              )}
            </>
          )}
          <MDSTooltip
            config={{
              label: "Replace",
              placement: MDSTooltipPlacement.Top,
            }}
          >
            <ReplaceToggleButton
              aria-label="Toggle replace"
              size={MDSIconButtonSize.Small}
              iconKind={MDSIconKind.Replace}
              isToggled={!!findInNote?.isReplaceMode}
              onClick={findInNote?.toggleReplaceMode}
            />
          </MDSTooltip>
          <MDSTooltip
            config={{
              label: "Dismiss",
              placement: MDSTooltipPlacement.Top,
            }}
          >
            <MDSIconButton
              aria-label="Close find in note"
              size={MDSIconButtonSize.Small}
              iconKind={MDSIconKind.Exit}
              onClick={close}
            />
          </MDSTooltip>
        </FindRow>
        {!!findInNote?.isReplaceMode && (
          <ReplacePanel>
            <ReplaceInputRow>
              <Expanding>
                <FindInput
                  aria-label="Replace with"
                  ref={replaceInputRef}
                  variant={MDSTextFieldVariant.Form}
                  placeholder="Replace with"
                  value={findInNote.replacementText}
                  onChange={findInNote.setReplacementText}
                  inputProps={{
                    onKeyDown: handleReplaceInputKeyDown,
                  }}
                />
              </Expanding>
            </ReplaceInputRow>
            <ReplaceActions>
              <MDSButton
                size={MDSButtonSize.Medium}
                variant={MDSButtonVariant.Transparent}
                isDisabled={!canReplace}
                onPress={store?.replaceAllFindInNoteResults}
              >
                Replace all
              </MDSButton>
              <MDSButton
                size={MDSButtonSize.Medium}
                variant={MDSButtonVariant.Default}
                isDisabled={!canReplace}
                onPress={store?.replaceCurrentFindInNoteResult}
              >
                Replace
              </MDSButton>
            </ReplaceActions>
          </ReplacePanel>
        )}
      </StyledOverlay>
    </StyledPopover>
  );
});

const StyledPopover = styled(MDSPopover)`
  position: relative;
`;

const StyledOverlay = styled.div<{ $isOpen: boolean }>`
  background-color: ${({ theme }) => theme.colors.surface.modal};
  border-radius: ${({ theme }) => theme.borderRadius.xlarge};
  padding: ${({ theme }) => `0 ${theme.spacing[3]} 0 ${theme.spacing[5]}`};
  box-sizing: border-box;
  box-shadow: ${({ theme }) => theme.shadows.surface};
  border: 1px solid ${({ theme }) => theme.colors.border.surface};
  border-radius: ${({ theme }) => theme.borderRadius.large};
  width: 420px;
  max-width: 90vw;
  overflow: hidden; /* clip inner scrollbar to rounded corners */
  ${({ $isOpen }) => ($isOpen ? `display: flex;` : `display: none;`)}
  flex-direction: column;
`;

const FindRow = styled.div`
  align-items: center;
  display: flex;
  height: 40px;
  width: 100%;
`;

const ReplacePanel = styled.div`
  border-top: 1px solid ${({ theme }) => theme.colors.border.surface};
  display: flex;
  flex-direction: column;
  padding-bottom: ${({ theme }) => theme.spacing[3]};
  width: 100%;
`;

const ReplaceInputRow = styled.div`
  display: flex;
  height: 40px;
  width: 100%;
`;

const ReplaceActions = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing[2]};
  justify-content: flex-end;
`;

const Expanding = styled.div`
  flex: 1;
  min-width: 0;
`;

const FindInput = styled(MDSTextField)`
  border: unset;
  background-color: transparent;
  padding: 0;
`;

const MatchCounterText = styled(MDSText)`
  margin: auto ${({ theme }) => theme.spacing[1]} auto ${({ theme }) => theme.spacing[1]};
  white-space: nowrap;
`;

const ReplaceToggleButton = styled(MDSIconButton)`
  .mds-icon {
    transform: scale(1.14);
    transform-origin: center;
  }
`;
